/**
 * @fileoverview Boter Kaas en Eieren Game Screen
 * @description Active game screen with lobby, waiting, and gameplay states
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Animated,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useTicTacToe,
  TicTacToeBoard,
  GameStatusBar,
  GameResultOverlay,
  BoardPosition,
  AIDifficulty,
} from "@/modules/tic-tac-toe";
import { notifyTeamOfPoints } from "@/modules/games/utils/teamNotifications";
import { RealtimeChannel } from "@supabase/supabase-js";

type SessionType = "solo" | "versus";
type SessionStatus = "waiting" | "active" | "completed" | "cancelled";

interface GameSession {
  id: string;
  game_slug: string;
  created_by: string;
  participants: Array<{
    user_id: string;
    role: string;
    symbol: string;
    name?: string;
  }>;
  mode: "free" | "weekend";
  session_type: SessionType;
  status: SessionStatus;
  weekend_id: string | null;
  game_state: Record<string, unknown> | null;
  game_config?: Record<string, unknown>;
}

/**
 * TicTacToeGameScreen - Active game with lobby support
 * Uses activeProfile to support guardian mode - when a guardian switches to
 * a dependent, games are played as that dependent.
 */
export default function TicTacToeGameScreen() {
  const colors = useThemeColors();
  const { sessionId } = useLocalSearchParams<{ sessionId: string }>();
  const { user, activeProfile, userTeam } = useAuth();
  
  // Use activeProfile for game actions (supports guardian mode)
  const targetUserId = activeProfile?.id;
  const targetProfile = activeProfile;

  // Session state
  const [session, setSession] = useState<GameSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Game state
  const [sessionType, setSessionType] = useState<SessionType>("solo");
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>("medium");
  const [showResult, setShowResult] = useState(false);
  const [sessionMode, setSessionMode] = useState<"free" | "weekend">("free");
  const [weekendId, setWeekendId] = useState<string | null>(null);

  // Lobby state
  const [isReady, setIsReady] = useState(false);
  const [opponentReady, setOpponentReady] = useState(false);
  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Series state
  const [showNextGameModal, setShowNextGameModal] = useState(false);
  const [nextGameReady, setNextGameReady] = useState(false);
  const [opponentNextGameReady, setOpponentNextGameReady] = useState(false);
  const [lastGameResult, setLastGameResult] = useState<"win" | "loss" | "draw" | null>(null);

  // Realtime channel ref
  const channelRef = useRef<RealtimeChannel | null>(null);
 
  /**
   * Determine user's role in the game (uses targetUserId for guardian mode)
   */
  const isChallenger = session?.created_by === targetUserId;
  const isChallenged = session?.game_state?.challenged_user_id === targetUserId;
  const challengerName = session?.game_state?.challenger_name as string | undefined;
  const challengedName = session?.game_state?.challenged_user_name as string | undefined;

  /**
   * Series info from game state
   */
  const gameState = session?.game_state as Record<string, unknown> | null;
  const seriesType = gameState?.series_type as "single" | "best_of_3" | "best_of_5" | undefined;
  const gamesToWin = (gameState?.games_to_win as number) ?? 1;
  const maxGames = (gameState?.max_games as number) ?? 1;
  const currentGame = (gameState?.current_game as number) ?? 1;
  const seriesScore = (gameState?.series_score as Record<string, number>) ?? {};
  const isSeries = seriesType && seriesType !== "single";
  
  // Calculate series winner (uses targetUserId for guardian mode)
  const mySeriesScore = targetUserId ? (seriesScore[targetUserId] ?? 0) : 0;
  const opponentId = isChallenger 
    ? (gameState?.challenged_user_id as string) 
    : session?.created_by;
  const opponentSeriesScore = opponentId ? (seriesScore[opponentId] ?? 0) : 0;
  const seriesWinner = mySeriesScore >= gamesToWin ? "me" : opponentSeriesScore >= gamesToWin ? "opponent" : null;
  const isSeriesOver = !!seriesWinner;

  /**
   * Load session and subscribe to realtime updates
   */
  useEffect(() => {
    if (!sessionId || !targetUserId) return;

    const loadSession = async () => {
      setSessionLoading(true);
      setSessionError(null);

      try {
        const { data, error } = await supabase
          .from("game_sessions")
          .select("*")
          .eq("id", sessionId)
          .single();

        if (error) throw error;
        if (!data) throw new Error("Spel niet gevonden");

        setSession(data as GameSession);
        setSessionType((data.session_type as SessionType) ?? "solo");
        setSessionMode(data.mode as "free" | "weekend");
        setWeekendId(data.weekend_id);

        // Check if we're ready based on game_state
        const gameState = data.game_state as Record<string, unknown> | null;
        if (isChallenger && gameState?.challenger_ready) {
          setIsReady(true);
        }
        if (isChallenged && gameState?.challenged_ready) {
          setIsReady(true);
        }
        setOpponentReady(
          isChallenger
            ? !!gameState?.challenged_ready
            : !!gameState?.challenger_ready
        );
      } catch (err) {
        console.error("Error loading session:", err);
        setSessionError(
          err instanceof Error ? err.message : "Kon spel niet laden"
        );
      } finally {
        setSessionLoading(false);
      }
    };

    loadSession();

    // Subscribe to session updates
    channelRef.current = supabase
      .channel(`game_session:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "game_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const updatedSession = payload.new as GameSession;
          const previousStatus = session?.status;
          setSession(updatedSession);

          // Update ready states
          const newGameState = updatedSession.game_state as Record<string, unknown> | null;
          if (isChallenger) {
            setOpponentReady(!!newGameState?.challenged_ready);
            setOpponentNextGameReady(!!newGameState?.next_game_challenged_ready);
          } else {
            setOpponentReady(!!newGameState?.challenger_ready);
            setOpponentNextGameReady(!!newGameState?.next_game_challenger_ready);
          }
          
          // If session was just completed (status changed to completed), show result modal
          if (previousStatus === "active" && updatedSession.status === "completed") {
            const isSeriesGame = newGameState?.series_type && newGameState?.series_type !== "single";
            const currentSeriesScore = newGameState?.series_score as Record<string, number> ?? {};
            const gamesToWin = newGameState?.games_to_win as number ?? 1;
            const myScore = currentSeriesScore[targetUserId] ?? 0;
            const opponentId = isChallenger ? newGameState?.challenged_user_id as string : updatedSession.created_by;
            const opponentScore = opponentId ? (currentSeriesScore[opponentId] ?? 0) : 0;
            const seriesIsOver = myScore >= gamesToWin || opponentScore >= gamesToWin;
            
            if (!isSeriesGame || seriesIsOver) {
              setShowResult(true);
            } else {
              setShowNextGameModal(true);
            }
          }
          
        }
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [sessionId, targetUserId, isChallenger, isChallenged]);

  /**
   * Pulse animation for waiting
   */
  useEffect(() => {
    if (session?.status === "waiting") {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [session?.status, pulseAnim]);

  /**
   * Handle ready toggle (uses targetUserId for guardian mode)
   */
  const handleReady = async () => {
    if (!session || !targetUserId) return;

    try {
      const gameState = (session.game_state as Record<string, unknown>) ?? {};
      const newReadyState = !isReady;
      setIsReady(newReadyState);

      const updatedGameState = {
        ...gameState,
        [isChallenger ? "challenger_ready" : "challenged_ready"]: newReadyState,
      };

      // Check if both are ready
      const bothReady =
        (isChallenger && newReadyState && gameState.challenged_ready) ||
        (isChallenged && newReadyState && gameState.challenger_ready);

      if (bothReady) {
        // Start the game! Use stored players from game_state (set during challenge creation)
        const storedPlayers = gameState.players as { X: string; O: string } | undefined;
        const challengerId = session.created_by;
        const challengedId = gameState.challenged_user_id as string;
        
        // Use stored players if available, otherwise fall back to challenger=X
        const players = storedPlayers || {
          X: challengerId,
          O: challengedId,
        };

        await supabase
          .from("game_sessions")
          .update({
            status: "active",
            game_state: {
              ...updatedGameState,
              board: [null, null, null, null, null, null, null, null, null],
              current_turn: "X",
              status: "playing",
              winner: null,
              win_line: null,
              move_count: 0,
              last_move_at: null,
              players: players,
              move_history: [],
            },
          })
          .eq("id", session.id);
      } else {
        await supabase
          .from("game_sessions")
          .update({
            game_state: updatedGameState,
          })
          .eq("id", session.id);
      }
    } catch (err) {
      console.error("Error updating ready state:", err);
      setIsReady(!isReady); // Revert on error
    }
  };

  /**
   * Handle decline (for challenged user)
   */
  const handleDecline = async () => {
    if (!session) return;

    Alert.alert(
      "Uitdaging afwijzen",
      "Weet je zeker dat je deze uitdaging wilt afwijzen?",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Afwijzen",
          style: "destructive",
          onPress: async () => {
            try {
              await supabase
                .from("game_sessions")
                .update({ status: "cancelled" })
                .eq("id", session.id);

              // Notify challenger (use targetProfile for guardian mode)
              if (session.created_by) {
                await supabase.from("notifications").insert({
                  user_id: session.created_by,
                  type: "game_declined",
                  notification_type: "game_declined",
                  title: "Uitdaging afgewezen",
                  body: `${targetProfile?.full_name ?? "Iemand"} heeft je uitdaging voor Boter Kaas en Eieren afgewezen.`,
                  action_data: { session_id: session.id },
                  sender_id: targetUserId,
                  lottie_animation: "Loudly_Crying_Emoji",
                });
              }

              router.back();
            } catch (err) {
              console.error("Error declining challenge:", err);
            }
          },
        },
      ]
    );
  };

  /**
   * Handle cancel (for challenger)
   */
  const handleCancel = async () => {
    if (!session) return;

    Alert.alert(
      "Uitdaging annuleren",
      "Weet je zeker dat je deze uitdaging wilt annuleren?",
      [
        { text: "Nee", style: "cancel" },
        {
          text: "Ja, annuleren",
          style: "destructive",
          onPress: async () => {
            try {
              await supabase
                .from("game_sessions")
                .update({ status: "cancelled" })
                .eq("id", session.id);
              router.back();
            } catch (err) {
              console.error("Error cancelling challenge:", err);
            }
          },
        },
      ]
    );
  };

  /**
   * Handle ready for next game in a series
   */
  const handleReadyForNextGame = async () => {
    if (!session || !targetUserId) return;

    setNextGameReady(true);

    try {
      const currentState = session.game_state as Record<string, unknown>;
      const readyField = isChallenger ? "next_game_challenger_ready" : "next_game_challenged_ready";

      await supabase
        .from("game_sessions")
        .update({
          game_state: {
            ...currentState,
            [readyField]: true,
          },
        })
        .eq("id", session.id);
    } catch (err) {
      console.error("Error setting next game ready:", err);
      setNextGameReady(false);
    }
  };

  /**
   * Start the next game in the series
   */
  const startNextGame = useCallback(async () => {
    if (!session) return;

    try {
      const currentState = session.game_state as Record<string, unknown>;
      const currentGameNum = (currentState.current_game as number) ?? 1;

      // Reset game board and ready states
      await supabase
        .from("game_sessions")
        .update({
          game_state: {
            ...currentState,
            // Reset board
            board: Array(9).fill(null),
            current_turn: "X",
            status: "playing",
            winner: null,
            win_line: null,
            move_count: 0,
            // Clear next game ready states
            next_game_challenger_ready: false,
            next_game_challenged_ready: false,
            last_game_winner: null,
            last_game_draw: null,
          },
          status: "active",
        })
        .eq("id", session.id);

      // Reset local states
      setShowNextGameModal(false);
      setNextGameReady(false);
      setOpponentNextGameReady(false);
      setLastGameResult(null);
      
      // The useTicTacToe hook will pick up the new game state via realtime
    } catch (err) {
      console.error("Error starting next game:", err);
    }
  }, [session]);

  /**
   * Auto-start next game when both players are ready
   */
  useEffect(() => {
    if (showNextGameModal && nextGameReady && opponentNextGameReady) {
      // Small delay for UI feedback
      const timer = setTimeout(() => {
        startNextGame();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showNextGameModal, nextGameReady, opponentNextGameReady, startNextGame]);


  /**
   * Handle game end (uses targetUserId for guardian mode)
   */
  const handleGameEnd = useCallback(
    async (result: {
      winner_id: string | null;
      is_draw: boolean;
      move_count: number;
    }) => {
      if (!targetUserId || !session) return;

      const isWinner = result.winner_id === targetUserId;
      const isLoser = result.winner_id !== null && result.winner_id !== targetUserId;
      
      // Set last game result for UI
      setLastGameResult(isWinner ? "win" : result.is_draw ? "draw" : "loss");

      // Get current series state from session
      const currentGameState = session.game_state as Record<string, unknown> | null;
      const currentSeriesType = currentGameState?.series_type as string | undefined;
      const currentGamesToWin = (currentGameState?.games_to_win as number) ?? 1;
      const currentSeriesScore = { ...(currentGameState?.series_score as Record<string, number> ?? {}) };
      const currentGameNum = (currentGameState?.current_game as number) ?? 1;
      const challengedUserId = currentGameState?.challenged_user_id as string;
      
      // Update series score
      if (result.winner_id && result.winner_id !== "ai") {
        currentSeriesScore[result.winner_id] = (currentSeriesScore[result.winner_id] ?? 0) + 1;
      }
      
      // Check if series is over (uses targetUserId for guardian mode)
      const myNewScore = currentSeriesScore[targetUserId] ?? 0;
      const opponentIdForScore = isChallenger ? challengedUserId : session.created_by;
      const opponentNewScore = opponentIdForScore ? (currentSeriesScore[opponentIdForScore] ?? 0) : 0;
      const seriesIsOver = myNewScore >= currentGamesToWin || opponentNewScore >= currentGamesToWin;
      const isSeriesGame = currentSeriesType && currentSeriesType !== "single";

      try {
        // Update game session with new series score
        if (isSeriesGame) {
          await supabase
            .from("game_sessions")
            .update({
              game_state: {
                ...currentGameState,
                series_score: currentSeriesScore,
                current_game: currentGameNum + 1,
                // Reset ready states for next game
                next_game_challenger_ready: false,
                next_game_challenged_ready: false,
                // Store last game result
                last_game_winner: result.winner_id,
                last_game_draw: result.is_draw,
              },
              status: seriesIsOver ? "completed" : "active",
            })
            .eq("id", session.id);
        }

        // Update user stats for active profile (count each individual game)
        const { data: existingStats } = await supabase
          .from("user_game_stats")
          .select("*")
          .eq("user_id", targetUserId)
          .eq("game_slug", "tic_tac_toe")
          .single();

        if (existingStats) {
          await supabase
            .from("user_game_stats")
            .update({
              games_played: existingStats.games_played + 1,
              games_won: existingStats.games_won + (isWinner ? 1 : 0),
              games_lost: existingStats.games_lost + (isLoser ? 1 : 0),
              games_drawn: existingStats.games_drawn + (result.is_draw ? 1 : 0),
              last_played_at: new Date().toISOString(),
              mode: sessionMode,
            })
            .eq("id", existingStats.id);
        } else {
          await supabase.from("user_game_stats").insert({
            user_id: targetUserId,
            game_slug: "tic_tac_toe",
            games_played: 1,
            games_won: isWinner ? 1 : 0,
            games_lost: isLoser ? 1 : 0,
            games_drawn: result.is_draw ? 1 : 0,
            mode: sessionMode,
          });
        }

        // If series is over and user won the series, award points
        const wonSeries = myNewScore >= currentGamesToWin;
        if (sessionMode === "weekend" && weekendId && seriesIsOver && wonSeries && userTeam?.id) {
          const { data: gameConfig } = await supabase
            .from("weekend_game_config")
            .select("points_config")
            .eq("weekend_id", weekendId)
            .eq("game_slug", "tic_tac_toe")
            .single();

          const points = (gameConfig?.points_config as { win?: number })?.win ?? 10;

          // Get current team score
          const { data: teamData } = await supabase
            .from("teams")
            .select("total_score")
            .eq("id", userTeam.id)
            .single();

          const previousTotal = teamData?.total_score ?? 0;
          const newTotal = previousTotal + points;

          // Update team total score
          await supabase
            .from("teams")
            .update({ total_score: newTotal })
            .eq("id", userTeam.id);

          // Insert score history entry (use targetUserId for guardian mode)
          await supabase.from("score_history").insert({
            team_id: userTeam.id,
            points: points,
            previous_total: previousTotal,
            new_total: newTotal,
            reason: `Boter Kaas en Eieren gewonnen (${myNewScore}-${opponentNewScore})`,
            game_type: "boter_kaas_eieren",
            awarded_by: targetUserId,
            awarded_to: targetUserId,
            weekend_id: weekendId,
          });

          // Update game session with points awarded
          await supabase
            .from("game_sessions")
            .update({ points_awarded: points })
            .eq("id", sessionId);

          // Notify team members (use targetUserId/targetProfile for guardian mode)
          await notifyTeamOfPoints({
            earnerId: targetUserId,
            earnerName: targetProfile?.full_name ?? "Iemand",
            teamId: userTeam.id,
            points,
            gameName: "Boter Kaas en Eieren",
            reason: "win",
            weekendId: weekendId ?? undefined,
            sessionId: sessionId ?? undefined,
            isWin: true,
          });
        }

        // Show appropriate modal
        if (isSeriesGame && !seriesIsOver) {
          // Show next game modal instead of result
          setShowNextGameModal(true);
          setNextGameReady(false);
          setOpponentNextGameReady(false);
        } else {
          // Single game or series is over - show result
          setShowResult(true);
        }
      } catch (err) {
        console.error("Error updating stats:", err);
        setShowResult(true);
      }
    },
    [targetUserId, targetProfile, session, weekendId, sessionMode, userTeam, sessionId, isChallenger]
  );

  // Initialize game hook (only when session is active)
  const {
    board,
    currentTurn,
    mySymbol,
    isMyTurn,
    gameStatus,
    winner,
    winLine,
    animatingCell,
    opponentInfo,
    moveCount,
    isLoading: gameLoading,
    error: gameError,
    makeMove,
    resetGame,
    forfeit,
  } = useTicTacToe({
    sessionId: sessionId ?? "", // Always pass sessionId - let the hook handle status internally
    mode: sessionType === "solo" ? "solo" : "versus",
    aiDifficulty,
    onGameEnd: handleGameEnd,
  });

  const handleCellPress = useCallback(
    (position: BoardPosition) => {
      makeMove(position);
    },
    [makeMove]
  );

  const handleForfeit = useCallback(() => {
    Alert.alert(
      "Spel opgeven",
      "Weet je zeker dat je het spel wilt opgeven?",
      [
        { text: "Annuleren", style: "cancel" },
        { text: "Opgeven", style: "destructive", onPress: () => forfeit() },
      ]
    );
  }, [forfeit]);

  /**
   * Handle rematch request - show modal and wait for other player
   */

  const handleExit = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(tabs)/games");
    }
  }, []);

  // Loading state
  if (sessionLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Spel laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (sessionError || !session) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle" size={64} color="#EF4444" />
          <Text style={[styles.errorText, { color: colors.text }]}>
            {sessionError ?? "Spel niet gevonden"}
          </Text>
          <TouchableOpacity style={styles.primaryButton} onPress={() => router.back()}>
            <Text style={styles.primaryButtonText}>Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Cancelled state
  if (session.status === "cancelled") {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        <View style={styles.centerContainer}>
          <Ionicons name="close-circle" size={64} color="#EF4444" />
          <Text style={[styles.titleText, { color: colors.text }]}>
            Spel geannuleerd
          </Text>
          <Text style={[styles.subtitleText, { color: colors.textSecondary }]}>
            Dit spel is geannuleerd of afgewezen.
          </Text>
          <TouchableOpacity style={styles.primaryButton} onPress={() => router.back()}>
            <Text style={styles.primaryButtonText}>Terug naar spellen</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Waiting/Lobby state
  if (session.status === "waiting") {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleExit} style={styles.headerButton}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Game Lobby
          </Text>
          <View style={styles.headerButton} />
        </View>

        <View style={styles.lobbyContainer}>
          {/* Game Icon */}
          <Animated.View style={[styles.gameIconContainer, { transform: [{ scale: pulseAnim }] }]}>
            <Text style={styles.gameIcon}>⭕</Text>
            <Text style={[styles.gameIcon, styles.gameIconOverlay]}>❌</Text>
          </Animated.View>

          <Text style={[styles.lobbyTitle, { color: colors.text }]}>
            Boter Kaas en Eieren
          </Text>

          {/* Challenger info */}
          {isChallenger && (
            <>
              <Text style={[styles.lobbySubtitle, { color: colors.textSecondary }]}>
                Wachten op {challengedName ?? "tegenstander"}...
              </Text>
              <View style={styles.statusContainer}>
                <View style={[styles.statusBadge, isReady && styles.statusBadgeReady]}>
                  <Ionicons
                    name={isReady ? "checkmark-circle" : "ellipse-outline"}
                    size={20}
                    color={isReady ? "#22C55E" : "#6B7280"}
                  />
                  <Text style={[styles.statusText, isReady && styles.statusTextReady]}>
                    Jij: {isReady ? "Klaar!" : "Niet klaar"}
                  </Text>
                </View>
                <View style={[styles.statusBadge, opponentReady && styles.statusBadgeReady]}>
                  <Ionicons
                    name={opponentReady ? "checkmark-circle" : "hourglass-outline"}
                    size={20}
                    color={opponentReady ? "#22C55E" : "#6B7280"}
                  />
                  <Text style={[styles.statusText, opponentReady && styles.statusTextReady]}>
                    {challengedName ?? "Tegenstander"}: {opponentReady ? "Klaar!" : "Wachten..."}
                  </Text>
                </View>
              </View>
            </>
          )}

          {/* Challenged user info */}
          {isChallenged && (
            <>
              <Text style={[styles.lobbySubtitle, { color: colors.textSecondary }]}>
                {challengerName ?? "Iemand"} daagt je uit!
              </Text>
              <View style={styles.statusContainer}>
                <View style={[styles.statusBadge, opponentReady && styles.statusBadgeReady]}>
                  <Ionicons
                    name={opponentReady ? "checkmark-circle" : "hourglass-outline"}
                    size={20}
                    color={opponentReady ? "#22C55E" : "#6B7280"}
                  />
                  <Text style={[styles.statusText, opponentReady && styles.statusTextReady]}>
                    {challengerName ?? "Uitdager"}: {opponentReady ? "Klaar!" : "Wachten..."}
                  </Text>
                </View>
                <View style={[styles.statusBadge, isReady && styles.statusBadgeReady]}>
                  <Ionicons
                    name={isReady ? "checkmark-circle" : "ellipse-outline"}
                    size={20}
                    color={isReady ? "#22C55E" : "#6B7280"}
                  />
                  <Text style={[styles.statusText, isReady && styles.statusTextReady]}>
                    Jij: {isReady ? "Klaar!" : "Niet klaar"}
                  </Text>
                </View>
              </View>
            </>
          )}

          {/* Action buttons */}
          <View style={styles.lobbyActions}>
            <TouchableOpacity
              style={[styles.readyButton, isReady && styles.readyButtonActive]}
              onPress={handleReady}
            >
              <Ionicons
                name={isReady ? "checkmark-circle" : "play-circle"}
                size={24}
                color="#FFFFFF"
              />
              <Text style={styles.readyButtonText}>
                {isReady ? "Klaar! Wachten..." : "Ik ben klaar!"}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.cancelButton}
              onPress={isChallenger ? handleCancel : handleDecline}
            >
              <Text style={styles.cancelButtonText}>
                {isChallenger ? "Annuleren" : "Afwijzen"}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Info */}
          <Text style={[styles.lobbyInfo, { color: colors.textSecondary }]}>
            Het spel begint automatisch als beide spelers klaar zijn
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Game loading state (for solo games)
  if (gameLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Spel laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (gameError) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle" size={64} color="#EF4444" />
          <Text style={[styles.errorText, { color: colors.text }]}>{gameError}</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={() => router.back()}>
            <Text style={styles.primaryButtonText}>Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Active game state
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={["top", "left", "right"]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleExit} style={styles.headerButton}>
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>

        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Boter Kaas en Eieren
          </Text>
          {sessionMode === "weekend" && (
            <View style={styles.weekendBadge}>
              <Text style={styles.weekendBadgeText}>Weekend</Text>
            </View>
          )}
        </View>

        {gameStatus === "playing" && (
          <TouchableOpacity onPress={handleForfeit} style={styles.headerButton}>
            <Ionicons name="flag" size={24} color="#EF4444" />
          </TouchableOpacity>
        )}
      </View>

      {/* Series Progress Indicator */}
      {isSeries && (
        <View style={styles.seriesIndicator}>
          <Text style={styles.seriesIndicatorLabel}>
            Spel {currentGame} • Eerste tot {gamesToWin}
          </Text>
          <View style={styles.seriesIndicatorScores}>
            <Text style={[styles.seriesIndicatorScore, mySeriesScore > opponentSeriesScore && styles.seriesIndicatorScoreLeading]}>
              {mySeriesScore}
            </Text>
            <Text style={styles.seriesIndicatorDash}>-</Text>
            <Text style={[styles.seriesIndicatorScore, opponentSeriesScore > mySeriesScore && styles.seriesIndicatorScoreLeading]}>
              {opponentSeriesScore}
            </Text>
          </View>
        </View>
      )}

      {/* Game Status Bar */}
      <GameStatusBar
        currentTurn={currentTurn}
        mySymbol={mySymbol}
        isMyTurn={isMyTurn}
        gameStatus={gameStatus}
        winner={winner}
        opponentInfo={opponentInfo}
        myInfo={{
          name: targetProfile?.full_name ?? "Jij",
          avatar_url: targetProfile?.avatar_url,
        }}
        moveCount={moveCount}
      />

      {/* Game Board */}
      <View style={styles.boardContainer}>
        <TicTacToeBoard
          board={board}
          disabled={gameStatus !== "playing" || !isMyTurn}
          winLine={winLine}
          onCellPress={handleCellPress}
          isMyTurn={isMyTurn}
          animatingCell={animatingCell}
        />
      </View>

      {/* Game Info */}
      <View style={styles.infoContainer}>
        <Text style={[styles.infoText, { color: colors.textSecondary }]}>
          {gameStatus === "playing" && isMyTurn
            ? "Tik op een leeg vakje om je zet te doen"
            : gameStatus === "playing"
            ? "Wacht op je tegenstander..."
            : null}
        </Text>
      </View>

      {/* Result Overlay - only shown for single games or series end */}
      <GameResultOverlay
        visible={showResult}
        gameStatus={gameStatus}
        winner={winner}
        mySymbol={mySymbol}
        opponentName={opponentInfo?.name ?? "Tegenstander"}
        onExit={handleExit}
        onClose={() => setShowResult(false)}
        seriesInfo={isSeries && isSeriesOver ? {
          myScore: mySeriesScore,
          opponentScore: opponentSeriesScore,
          isSeriesWinner: seriesWinner === "me",
          isSeriesLoser: seriesWinner === "opponent",
          gamesToWin: gamesToWin,
        } : undefined}
      />

      {/* Next Game Modal - for series play */}
      <Modal
        visible={showNextGameModal}
        transparent
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.nextGameModalBackdrop}>
          <Animated.View style={styles.nextGameModalContainer}>
            {/* Result Badge */}
            <View style={[
              styles.resultBadge,
              lastGameResult === "win" && styles.resultBadgeWin,
              lastGameResult === "loss" && styles.resultBadgeLoss,
              lastGameResult === "draw" && styles.resultBadgeDraw,
            ]}>
              <Text style={styles.resultBadgeText}>
                {lastGameResult === "win" ? "🎉 Gewonnen!" : lastGameResult === "draw" ? "🤝 Gelijkspel" : "😔 Verloren"}
              </Text>
            </View>

            {/* Series Score */}
            <Text style={styles.nextGameTitle}>Serie Stand</Text>
            <View style={styles.seriesScoreContainer}>
              <View style={styles.seriesScoreItem}>
                <Text style={styles.seriesScoreNumber}>{mySeriesScore}</Text>
                <Text style={styles.seriesScoreName}>Jij</Text>
              </View>
              <Text style={styles.seriesScoreDivider}>-</Text>
              <View style={styles.seriesScoreItem}>
                <Text style={styles.seriesScoreNumber}>{opponentSeriesScore}</Text>
                <Text style={styles.seriesScoreName}>{opponentInfo?.name ?? "Tegenstander"}</Text>
              </View>
            </View>

            {/* Game progress */}
            <Text style={styles.gameProgressText}>
              Spel {currentGame} van {maxGames} • Eerste tot {gamesToWin} wint
            </Text>

            {/* Ready Status */}
            <View style={styles.readyStatusContainer}>
              <View style={styles.readyStatusRow}>
                <Ionicons 
                  name={nextGameReady ? "checkmark-circle" : "ellipse-outline"} 
                  size={24} 
                  color={nextGameReady ? "#22C55E" : "#6B7280"} 
                />
                <Text style={styles.readyStatusText}>
                  Jij {nextGameReady ? "bent klaar" : "bent nog niet klaar"}
                </Text>
              </View>
              <View style={styles.readyStatusRow}>
                <Ionicons 
                  name={opponentNextGameReady ? "checkmark-circle" : "ellipse-outline"} 
                  size={24} 
                  color={opponentNextGameReady ? "#22C55E" : "#6B7280"} 
                />
                <Text style={styles.readyStatusText}>
                  {opponentInfo?.name ?? "Tegenstander"} {opponentNextGameReady ? "is klaar" : "is nog niet klaar"}
                </Text>
              </View>
            </View>

            {/* Actions */}
            {!nextGameReady ? (
              <TouchableOpacity
                style={styles.nextGameReadyButton}
                onPress={handleReadyForNextGame}
              >
                <Ionicons name="play" size={20} color="#FFFFFF" />
                <Text style={styles.nextGameReadyButtonText}>Klaar voor volgende spel!</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.waitingNextGame}>
                <ActivityIndicator color="#8B5CF6" size="small" />
                <Text style={styles.waitingNextGameText}>
                  {opponentNextGameReady ? "Starten..." : "Wachten op tegenstander..."}
                </Text>
              </View>
            )}

            {/* Exit option */}
            <TouchableOpacity
              style={styles.exitSeriesButton}
              onPress={() => {
                Alert.alert(
                  "Serie verlaten",
                  "Weet je zeker dat je de serie wilt verlaten? Je verliest de serie hiermee.",
                  [
                    { text: "Annuleren", style: "cancel" },
                    { 
                      text: "Verlaten", 
                      style: "destructive",
                      onPress: () => {
                        forfeit();
                        router.back();
                      }
                    },
                  ]
                );
              }}
            >
              <Text style={styles.exitSeriesButtonText}>Serie verlaten</Text>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </Modal>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
    justifyContent: "space-between",
  },
  headerButton: {
    padding: 12,
    width: 48,
  },
  headerCenter: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  weekendBadge: {
    backgroundColor: "#8B5CF6",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  weekendBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "600",
  },
  loadingText: {
    fontSize: 16,
  },
  errorText: {
    fontSize: 18,
    textAlign: "center",
  },
  titleText: {
    fontSize: 24,
    fontWeight: "bold",
    marginTop: 16,
  },
  subtitleText: {
    fontSize: 16,
    textAlign: "center",
  },
  primaryButton: {
    backgroundColor: "#8B5CF6",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    marginTop: 16,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  // Lobby styles
  lobbyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 16,
  },
  gameIconContainer: {
    width: 120,
    height: 120,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 16,
  },
  gameIcon: {
    fontSize: 80,
  },
  gameIconOverlay: {
    position: "absolute",
    fontSize: 40,
    top: 10,
    right: 10,
  },
  lobbyTitle: {
    fontSize: 28,
    fontWeight: "bold",
  },
  lobbySubtitle: {
    fontSize: 18,
    textAlign: "center",
  },
  statusContainer: {
    gap: 12,
    marginTop: 24,
    marginBottom: 16,
    width: "100%",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(107, 114, 128, 0.2)",
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 12,
  },
  statusBadgeReady: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
  },
  statusText: {
    fontSize: 16,
    color: "#9CA3AF",
    fontWeight: "500",
  },
  statusTextReady: {
    color: "#22C55E",
  },
  lobbyActions: {
    width: "100%",
    gap: 12,
    marginTop: 24,
  },
  readyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#8B5CF6",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 12,
  },
  readyButtonActive: {
    backgroundColor: "#22C55E",
  },
  readyButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#EF4444",
    fontSize: 16,
  },
  lobbyInfo: {
    fontSize: 14,
    textAlign: "center",
    marginTop: 24,
  },
  // Game styles
  boardContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  infoContainer: {
    padding: 16,
    alignItems: "center",
  },
  infoText: {
    fontSize: 14,
    textAlign: "center",
  },
  // Next game modal styles
  nextGameModalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  nextGameModalContainer: {
    backgroundColor: "#1F2937",
    borderRadius: 24,
    padding: 24,
    width: "100%",
    maxWidth: 360,
    alignItems: "center",
  },
  resultBadge: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 20,
    marginBottom: 24,
  },
  resultBadgeWin: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
  },
  resultBadgeLoss: {
    backgroundColor: "rgba(239, 68, 68, 0.2)",
  },
  resultBadgeDraw: {
    backgroundColor: "rgba(245, 158, 11, 0.2)",
  },
  resultBadgeText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  nextGameTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#9CA3AF",
    marginBottom: 8,
  },
  seriesScoreContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  seriesScoreItem: {
    alignItems: "center",
    minWidth: 80,
  },
  seriesScoreNumber: {
    fontSize: 48,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  seriesScoreName: {
    fontSize: 14,
    color: "#9CA3AF",
    marginTop: 4,
  },
  seriesScoreDivider: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#6B7280",
    marginHorizontal: 16,
  },
  gameProgressText: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 24,
  },
  readyStatusContainer: {
    width: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    gap: 12,
  },
  readyStatusRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  readyStatusText: {
    fontSize: 14,
    color: "#D1D5DB",
  },
  nextGameReadyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#8B5CF6",
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    gap: 8,
    width: "100%",
  },
  nextGameReadyButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  waitingNextGame: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    paddingVertical: 16,
    width: "100%",
  },
  waitingNextGameText: {
    color: "#9CA3AF",
    fontSize: 16,
  },
  exitSeriesButton: {
    paddingVertical: 12,
    marginTop: 8,
  },
  exitSeriesButtonText: {
    color: "#EF4444",
    fontSize: 14,
  },
  // Series indicator styles
  seriesIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "rgba(139, 92, 246, 0.1)",
    gap: 16,
  },
  seriesIndicatorLabel: {
    fontSize: 12,
    color: "#9CA3AF",
  },
  seriesIndicatorScores: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  seriesIndicatorScore: {
    fontSize: 16,
    fontWeight: "600",
    color: "#9CA3AF",
    minWidth: 20,
    textAlign: "center",
  },
  seriesIndicatorScoreLeading: {
    color: "#8B5CF6",
  },
  seriesIndicatorDash: {
    fontSize: 16,
    color: "#6B7280",
  },
  // Rematch modal styles
  rematchDescription: {
    fontSize: 16,
    color: "#D1D5DB",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 24,
  },
  rematchButtonsContainer: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginBottom: 8,
  },
  rematchButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  rematchButtonAccept: {
    backgroundColor: "#22C55E",
  },
  rematchButtonDecline: {
    backgroundColor: "#6B7280",
  },
  rematchButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});
